import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Stack, Button, ButtonGroup } from 'react-bootstrap';
import { Routes, Route, Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import TransactionTable from './components/TransactionTable';
import Summary from './components/Summary';
import FilterControls from './components/FilterControls';
import BulkUploadModal from './components/BulkUploadModal';
import LogModal from './components/LogModal';
import PayrollProcessorModal from './components/PayrollProcessorModal';
import InvoiceImpactModal from './components/InvoiceImpactModal';
import Login from './components/Login';
import CommissionsPage from './components/CommissionsPage';
import KpiUnpaidPage from './components/KpiUnpaidPage';
import AnalyticsPage from './components/AnalyticsPage';
import IncomeStatement from './components/IncomeStatement'; 
import GlobalWalletPage from './components/GlobalWalletPage'; 
import PortfolioDashboard from './components/PortfolioDashboard';
import DriverPayments from './components/DriverPayments';
import TransportDashboard from './transport/TransportDashboard'; 

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useJsApiLoader } from '@react-google-maps/api';
import { getTransactions, createTransaction, updateTransaction as apiUpdate, deleteTransaction as apiDelete, getLogsByTransactionId, updateTransactionsBulk, checkInvoiceImpact } from './api';
import { handleError } from './utils/errorHandler';
import useAuthStore, { isSuperAdminUser, SUPER_ADMIN_EMAILS } from './store/useAuthStore';
import SuperAdminImpersonator, { openSuperAdminModal } from './components/SuperAdminImpersonator';

import PortalLayout from './transport/portal/PortalLayout';
import PortalHome from './transport/portal/PortalHome';
import NewTripPage from './transport/portal/NewTripPage';
import MyTripsPage from './transport/portal/MyTripsPage';
import DriverPortal from './transport/portal/DriverPortal'; // NUEVO
import ProviderPortal from './transport/portal/ProviderPortal'; // NUEVO
import RegisterDriver from './components/RegisterDriver';
import DriverOnboarding from './transport/portal/DriverOnboarding';
import ResetPassword from './ResetPassword';
import AssistantChatWidget from './transport/components/AssistantChatWidget';
import PaymentSuccessPage from './components/PaymentSuccessPage';

// --- NUEVOS IMPORTS DE ASISTENCIA ---
import MarkAttendance from './components/Attendance/MarkAttendance';
import { AdminAttendance } from './components/Attendance/AdminAttendance';
import { ShiftManager } from './components/Attendance/ShiftManager';
import { UserManagement } from './components/Attendance/UserManagement';

// Pin Google Maps to version 3.64 to preserve drawing library functionality
const LIBRARIES = ['places', 'drawing'];

// A component to protect routes
const PrivateRoute = ({ children, requiredRole = null, requiredEmail = null }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const role = (user?.role || '').toLowerCase().trim();

  // Restricción por email específico (Solo si se pide explícitamente)
  if (requiredEmail && user?.email?.toLowerCase() !== requiredEmail.toLowerCase()) {
    // Si es de operaciones, mandarlo a su panel en lugar de portal clientes
    if (role === 'operaciones') return <Navigate to="/admin/asistencia" />;
    return <Navigate to="/portal" />;
  }

  // Si se requiere rol de admin y el usuario no lo tiene, redirigir según su capacidad
  // Nota: 'locutor' también tiene acceso a /transport/* (panel de operaciones)
  if (requiredRole === 'admin' && role !== 'admin' && role !== 'gestor' && role !== 'operaciones' && role !== 'locutor') {
    // Si tiene ID de chofer, al portal del conductor (prioridad máxima choferes)
    if (user?.linked_driver_id) return <Navigate to="/driver" />;
    if (role === 'driver') return <Navigate to="/driver" />;
    if (role === 'worker') return <Navigate to="/reloj" />;
    if (role === 'provider') return <Navigate to="/provider" />;
    return <Navigate to="/portal" />;
  }

  return children;
};

// Componente para redirigir desde Login o Root según el rol
const RoleBasedRedirect = () => {
  const { user, isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  const role = (user?.role || '').toLowerCase().trim();
  
  // 1. Jefa de Operaciones (Prioridad: Su panel de control)
  if (role === 'operaciones') {
    return <Navigate to="/admin/asistencia" replace />;
  }

  // 2. Gestor: debe marcar en el Reloj Control primero
  if (role === 'gestor') {
    return <Navigate to="/reloj" replace />;
  }

  // 3. PRIORIDAD MÁXIMA CHOFERES: Si tiene un conductor vinculado, va al Portal de Conductor
  if (user?.linked_driver_id) {
    console.log("Redirigiendo a Portal de Conductor (ID Vinculado)");
    return <Navigate to="/driver" replace />;
  }

  // 4. Roles de Oficina / Administración Finanzas (solo admin puro)
  if (role === 'admin') {
    return <Navigate to="/" replace />;
  } 
  
  // 4. Roles de Terreno / Portal Conductor (por rol explícito)
  if (role === 'driver') {
    return <Navigate to="/driver" replace />;
  }

  // 5. Roles de Terreno / Reloj
  if (role === 'locutor' || role === 'worker') {
    return <Navigate to="/reloj" replace />;
  } 

  // 6. Roles del Portal Corporativo B2B
  if (role === 'passenger' || role === 'requester') {
    return <Navigate to="/portal" replace />;
  }

  // 7. Roles de Proveedor (NUEVO)
  if (role === 'provider') {
    return <Navigate to="/provider" replace />;
  }

  // Fallback seguro
  return <Navigate to="/reloj" replace />;
};

// Wrapper para el Admin de Asistencia
const AttendanceAdminWrapper = () => {
  const { user, logout } = useAuthStore();
  const isLocutor = user?.role === 'locutor';
  const [subTab, setSubTab] = useState(isLocutor ? 'shifts' : 'reports');
  
  const isFacil = SUPER_ADMIN_EMAILS.includes(user?.email?.trim()?.toLowerCase());
  const isOps = user?.role?.toLowerCase() === 'operaciones';
  const isAdmin = ['admin', 'gestor', 'operaciones'].includes(user?.role?.toLowerCase());

  return (
    <Container style={{ maxWidth: '95%' }} className="py-4">
      <div className="d-flex justify-between align-items-center mb-4 pb-3 border-bottom border-slate-200">
        <div className="d-flex align-items-center gap-3">
          {isAdmin && <Link to="/"><Button variant="outline-secondary" size="sm" className="fw-bold px-3">← Volver al Inicio</Button></Link>}
          <h2 className="m-0 text-slate-800 fw-bold tracking-tight">Gestión de Asistencia</h2>
        </div>
        <div className="d-flex gap-2">
            <Link to="/reloj">
                <Button variant="success" className="fw-bold d-flex align-items-center gap-2 px-4 shadow-sm">
                    ⏱️ Ir a Marcar
                </Button>
            </Link>
            {!isLocutor && (
              <ButtonGroup className="shadow-sm me-2">
                <Button variant={subTab === 'reports' ? 'primary' : 'outline-primary'} onClick={() => setSubTab('reports')}>Reportes</Button>
                <Button variant={subTab === 'shifts' ? 'primary' : 'outline-primary'} onClick={() => setSubTab('shifts')}>Turnos</Button>
                {(isFacil || isOps) && (
                  <Button variant={subTab === 'users' ? 'primary' : 'outline-primary'} onClick={() => setSubTab('users')}>Personal</Button>
                )}
              </ButtonGroup>
            )}
            <Button variant="danger" className="fw-bold px-4 shadow-sm" onClick={logout}>
                Salir
            </Button>
        </div>
      </div>
      <div className="bg-slate-50 p-6 rounded-[3rem] border border-slate-100 shadow-inner"> 
        {subTab === 'reports' ? <AdminAttendance /> : subTab === 'shifts' ? <ShiftManager /> : <UserManagement />}
      </div>
    </Container>
  );
};

const MainApp = () => {
  const { user, originalAdminUser, logout } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [transactions, setTransactions] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filter, setFilter] = useState({ type: 'all', transactionType: 'all' });
  const [subCategoryFilter, setSubCategoryFilter] = useState(''); 
  const [searchQuery, setSearchQuery] = useState('');
  const [searchId, setSearchId] = useState(''); // Nuevo Estado para ID

  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkSubcategory, setBulkSubcategory] = useState(''); 

  const SUB_CATEGORIES_EXPENSE = [
    "Arriendo", "Cafetería y Snacks", "Combustible", "Comidas de Negocios", 
    "Comisiones Bancarias", "Equipos Tecnológicos", "Estacionamientos", 
    "Eventos Corporativos", "Insumos de Aseo", "Insumos de Oficina", "Internet", 
    "Lavado de Vehículos", "Legales y Notaría", "Mantenimiento Vehículos", 
    "Otros", "Patentes y Permisos", "Peajes y TAG", "Previred", 
    "Redes Sociales", "Servicios Básicos", "Suscripciones de Software", 
    "Telefonía Móvil/Fija", "Viajes de Negocios"
  ];

  const SUB_CATEGORIES_FINANCIAL = [
    "Compra Divisas (USD)", "Devolución de Gasto", "Inversión DAP", "Préstamo / Socios", "Remesa Internacional", "Transferencia Interna"
  ];

  // Effect to initialize filter from URL query parameters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const categoryParam = searchParams.get('category');
    const ageParam = searchParams.get('age');
    const transactionIdParam = searchParams.get('transactionId');
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');

    // Update date filters if present
    if (startDateParam) setStartDate(startDateParam);
    if (endDateParam) setEndDate(endDateParam);
    
    if (statusParam === 'unpaid') {
      setFilter(prev => ({ ...prev, type: 'unpaid', category: categoryParam, age: ageParam, transactionId: transactionIdParam }));
      setSearchParams({}, { replace: true });
    } else if (statusParam) {
      setFilter(prev => ({ ...prev, type: statusParam, category: categoryParam }));
      setSearchParams({}, { replace: true });
    } else if (categoryParam) {
      setFilter(prev => ({ ...prev, type: 'all', category: categoryParam }));
      setSearchParams({}, { replace: true });
    } else if (transactionIdParam) {
      setSearchQuery(transactionIdParam);
      setSearchParams({}, { replace: true });
    } else if (startDateParam || endDateParam) {
       setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [transactionLogs, setTransactionLogs] = useState([]);
  const [showTransactionLogsModal, setShowTransactionLogsModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortByCompletionDate, setSortByCompletionDate] = useState(false);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [impactModalLoading, setImpactModalLoading] = useState(false);
  const [impactModalData, setImpactModalData] = useState([]);
  const [impactTargetStatus, setImpactTargetStatus] = useState('completado');
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);
  const formRef = useRef(null);

  const canAccessPayroll = useMemo(() => {
    if (!user) return false;
    const email = (user.email || '').toLowerCase().trim();
    const role = (user.role || '').toLowerCase().trim();
    const perms = typeof user.permissions === 'object' && user.permissions !== null ? user.permissions : {};
    return email === 'alejandro.jerez@gmail.com' || role === 'admin' || perms.can_process_payroll === true;
  }, [user]);


  const fetchTransactions = useCallback(async () => {
    try {
      const params = { sort_by_completion_date: sortByCompletionDate };
      const data = await getTransactions(params);
      setTransactions(data);
    } catch (error) {
      handleError(error, "Error al cargar las transacciones.");
    }
  }, [sortByCompletionDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedIds([]);
  }, [filter, searchQuery, startDate, endDate, subCategoryFilter]); // Agregado subCategoryFilter

  useEffect(() => {
    if (showTransactionForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showTransactionForm]);

  useEffect(() => {
    if (highlightedTransactionId) {
      setViewMode('list');
      setTimeout(() => {
        const element = document.getElementById(`transaction-item-${highlightedTransactionId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight');
          setTimeout(() => {
            element.classList.remove('highlight');
            setHighlightedTransactionId(null);
          }, 2000);
        } else {
          setHighlightedTransactionId(null);
        }
      }, 100);
    }
  }, [highlightedTransactionId]);

  const addSingleTransaction = async (transaction) => {
    try {
      await createTransaction(transaction);
      await fetchTransactions();
      setShowTransactionForm(false);
      toast.success("¡Transacción agregada con éxito!");
    } catch (error) {
      handleError(error, "Error al crear la transacción.");
    }
  };

  const executeUpdateTransaction = async (updatedTransaction) => {
    try {
      await apiUpdate(updatedTransaction.id, updatedTransaction);
      await fetchTransactions();
      toast.info("¡Transacción actualizada!");
      setEditingTransaction(null);
      setShowTransactionForm(false);
      setShowImpactModal(false);
    } catch (error) {
      handleError(error, "Error al actualizar la transacción.");
    }
  };

  const updateTransaction = async (updatedTransaction) => {
    const original = transactions.find(t => t.id === updatedTransaction.id);
    if (updatedTransaction.status === 'completado' && original && original.status !== 'completado') {
      try {
        setImpactModalLoading(true);
        setShowImpactModal(true);
        setImpactTargetStatus('completado');
        setPendingConfirmAction(() => () => executeUpdateTransaction(updatedTransaction));
        const impact = await checkInvoiceImpact([updatedTransaction.id]);
        setImpactModalData(impact);
      } catch (err) {
        console.error("Error consultando impacto:", err);
        executeUpdateTransaction(updatedTransaction);
        setShowImpactModal(false);
      } finally {
        setImpactModalLoading(false);
      }
      return;
    }
    executeUpdateTransaction(updatedTransaction);
  };

  const deleteTransaction = async (id) => {
    const transactionToDelete = transactions.find(t => t.id === id);
    if (transactionToDelete && window.confirm("¿Estás seguro de que deseas eliminar esta transacción?")) {
      try {
        await apiDelete(id);
        await fetchTransactions();
        toast.error("Transacción eliminada.");
      } catch (error) {
        handleError(error, "Error al eliminar la transacción.");
      }
    }
  };

  const toggleComplete = async (id) => {
    const transaction = transactions.find(t => t.id === id);
    if (!transaction) return;
    const willComplete = transaction.status !== 'completado';
    const newStatus = willComplete ? 'completado' : 'pendiente';
    const updated = { ...transaction, status: newStatus };

    if (willComplete) {
      try {
        setImpactModalLoading(true);
        setShowImpactModal(true);
        setImpactTargetStatus('completado');
        setPendingConfirmAction(() => () => executeUpdateTransaction(updated));
        const impact = await checkInvoiceImpact([id]);
        setImpactModalData(impact);
      } catch (err) {
        console.error("Error consultando impacto:", err);
        executeUpdateTransaction(updated);
        setShowImpactModal(false);
      } finally {
        setImpactModalLoading(false);
      }
    } else {
      executeUpdateTransaction(updated);
    }
  };

  const cloneTransaction = (id) => {
    const transactionToClone = transactions.find(t => t.id === id);
    if (!transactionToClone) return;
    const clonedTransaction = { ...transactionToClone, id: undefined, description: `${transactionToClone.description} (Copia)`, status: 'pendiente' };
    handleSetEditing(clonedTransaction);
    toast.info('Transacción clonada. Ahora puedes editarla.');
  };

  const handleSetEditing = (transaction) => {
    setEditingTransaction(transaction);
    setShowTransactionForm(true);
  };

  const cancelEdit = () => {
    setEditingTransaction(null);
    setShowTransactionForm(false);
  };

  const handleFilterChange = (newFilterType) => {
    setFilter(prev => ({ ...prev, type: newFilterType, category: undefined }));
    setSubCategoryFilter(''); // Reset subcat
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('ids');
    newParams.delete('eerrColumn');
    setSearchParams(newParams);
  };

  const handleTypeFilterChange = (newType) => {
    setFilter(prev => ({ ...prev, transactionType: newType }));
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('ids');
    newParams.delete('eerrColumn');
    setSearchParams(newParams);
  };

  const handleCategoryFilterClick = (newCategory) => {
    setFilter(prev => ({ ...prev, category: newCategory === '' ? undefined : newCategory }));
    setSubCategoryFilter(''); // Reset subcat when cat changes
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('ids');
    newParams.delete('eerrColumn');
    setSearchParams(newParams);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const toggleTransactionForm = () => {
    setShowTransactionForm(!showTransactionForm);
    if (editingTransaction) {
      setEditingTransaction(null);
    }
  };

  const handleSummaryFilterClick = (filterType) => {
    setViewMode('table');
    handleFilterChange(filterType);
  };

  const handleSummaryCategoryFilterClick = (category) => {
    setViewMode('table');
    setFilter(prev => ({ ...prev, type: 'overdue', category: category }));
  };

  const handleSortToggle = () => {
    setSortByCompletionDate(prev => !prev);
  };

  const handleToggleSelection = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const executeBulkUpdate = async () => {
    try {
      await updateTransactionsBulk(selectedIds, bulkStatus || null, bulkCategory || null, bulkSubcategory || null);
      await fetchTransactions();
      toast.success("¡Transacciones actualizadas correctamente!");
      setSelectedIds([]); 
      setBulkStatus('');
      setBulkCategory('');
      setBulkSubcategory('');
      setShowImpactModal(false);
    } catch (error) {
      handleError(error, "Error al actualizar transacciones masivamente.");
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus && !bulkCategory) return;

    if (bulkStatus === 'completado') {
      try {
        setImpactModalLoading(true);
        setShowImpactModal(true);
        setImpactTargetStatus('completado');
        setPendingConfirmAction(() => () => executeBulkUpdate());
        const impact = await checkInvoiceImpact(selectedIds);
        setImpactModalData(impact);
      } catch (err) {
        console.error("Error consultando impacto masivo:", err);
        if (window.confirm(`¿Estás seguro de actualizar ${selectedIds.length} transacciones?`)) {
          executeBulkUpdate();
        }
        setShowImpactModal(false);
      } finally {
        setImpactModalLoading(false);
      }
      return;
    }

    if (!window.confirm(`¿Estás seguro de actualizar ${selectedIds.length} transacciones?`)) return;
    executeBulkUpdate();
  };

  const handleShowTransactionLogs = async (transactionId) => {
    try {
      const logs = await getLogsByTransactionId(transactionId);
      setTransactionLogs(logs);
      setShowTransactionLogsModal(true);
    } catch (error) {
      handleError(error, "Error al cargar el historial de la transacción.");
    }
  };

  const handleGoToTransaction = (transactionId) => {
    setShowLogModal(false);
    setShowTransactionLogsModal(false);
    setHighlightedTransactionId(transactionId);
  };


  const getLocalDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysAgoDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const hasActiveFilters = !!(
    searchId ||
    searchQuery ||
    startDate ||
    endDate ||
    (filter.type && filter.type !== 'all') ||
    (filter.transactionType && filter.transactionType !== 'all') ||
    filter.category ||
    subCategoryFilter ||
    searchParams.get('ids') ||
    searchParams.get('status') ||
    searchParams.get('category') ||
    searchParams.get('transactionId')
  );

  const oneWeekAgo = getDaysAgoDate(7);

  const filteredTransactions = transactions.filter(transaction => {
    // 1. Prioridad: Búsqueda por ID exacto
    if (searchId) {
        return transaction.id === parseInt(searchId);
    }

    const idsParam = searchParams.get('ids');
    if (idsParam) {
        const targetIds = idsParam.split(',').map(id => parseInt(id.trim()));
        return targetIds.includes(transaction.id);
    }

    const today = getLocalDate();
    const useAccrualDate = searchParams.get('useAccrualDate') === 'true';
    let matchesFilter = true;

    if (filter.transactionType !== 'all') {
        if (transaction.type !== filter.transactionType) {
            return false;
        }
    }

    switch (filter.type) {
      case 'pending': matchesFilter = transaction.status === 'pendiente'; break;
      case 'completado': matchesFilter = transaction.status === 'completado'; break;
      case 'overdue': matchesFilter = transaction.status !== 'completado' && transaction.date < today; break;
      case 'today': matchesFilter = transaction.date === today && (transaction.status === 'pendiente' || (transaction.status && transaction.status.toLowerCase() === 'por procesar')); break;
      case 'por procesar': matchesFilter = transaction.status && transaction.status.toLowerCase() === 'por procesar'; break;
      case 'esperando oc/hes': matchesFilter = transaction.status && transaction.status.toLowerCase() === 'esperando oc/hes'; break;
      case 'unpaid': matchesFilter = transaction.status !== 'completado'; break;
      default: matchesFilter = true; break;
    }

    if (matchesFilter && filter.category) {
      if (filter.category === 'Remuneraciones') {
        const cat = transaction.category;
        matchesFilter = cat === 'Remuneraciones' || cat === 'Salario';
      } else if (filter.category === 'Gastos Operacionales') {
         const cat = transaction.category || '';
         const catLower = cat.toLowerCase();
         const isExcluded = cat === 'Remuneraciones' || cat === 'Salario' ||
                           cat === 'Impuestos Directos' ||
                           catLower === 'particulares' || catLower === 'conductores';
         
         matchesFilter = transaction.type === 'egreso' && !isExcluded;
      } else {
        matchesFilter = transaction.category === filter.category;
      }

      // Filtro de Subcategoría
      if (matchesFilter && subCategoryFilter) {
          matchesFilter = transaction.subcategory === subCategoryFilter;
      }
    }

    if (matchesFilter && filter.age) {
      const getDaysOverdue = (transactionDate) => {
        const date = new Date(transactionDate);
        const diffTime = Math.abs(today - date);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      };
      const days = getDaysOverdue(transaction.date);

      switch (filter.age) {
        case '0-30 días': matchesFilter = days <= 30; break;
        case '31-60 días': matchesFilter = days > 30 && days <= 60; break;
        case '>60 días': matchesFilter = days > 60; break;
        default: break;
      }
    }

    if (matchesFilter && filter.transactionId) {
      matchesFilter = String(transaction.id) === filter.transactionId;
    }

    if (matchesFilter && searchQuery) {
      const query = searchQuery.toLowerCase();
      const searchableFields = [ transaction.description, transaction.category, transaction.entity_name, transaction.invoice_number, transaction.request_id, transaction.amount.toString(), transaction.notes, transaction.id.toString() ];
      matchesFilter = searchableFields.some(field => field && field.toLowerCase().includes(query));
    }

    const filterDate = (useAccrualDate && transaction.accrual_date) ? transaction.accrual_date : transaction.date;

    if (matchesFilter && startDate) {
      matchesFilter = filterDate >= startDate;
    }
    if (matchesFilter && endDate) {
      matchesFilter = matchesFilter && filterDate <= endDate;
    }

    // Si no hay ningún filtro activo, por defecto mostramos sólo de 1 semana hacia atrás
    if (matchesFilter && !hasActiveFilters) {
      matchesFilter = filterDate >= oneWeekAgo;
    }

    return matchesFilter;
  }).sort((a, b) => {
    if (sortByCompletionDate) {
        return 0; 
    }
    return b.id - a.id;
  });

  let transactionsWithRunningBalance = [];
  const transactionsWithBalanceImpact = filteredTransactions.map(transaction => {
    let balanceImpact = transaction.type === 'ingreso'
      ? (transaction.amount + (transaction.dap_interest || 0) - (transaction.cc_commission || 0) - (transaction.penalizaciones_booking || 0))
      : -(transaction.amount - (transaction.cc_commission || 0)); 

    const eerrColumn = searchParams.get('eerrColumn');
    if (eerrColumn) {
        balanceImpact = 0; 
        
        const amount = transaction.amount || 0;
        const dap = transaction.dap_interest || 0;
        const commission = transaction.cc_commission || 0;

        if (eerrColumn === 'ingresosDap') {
            balanceImpact = dap;
        } else if (eerrColumn === 'costoSalarios' || eerrColumn.startsWith('costoHHEE') || eerrColumn === 'impuestosDirectos' || eerrColumn === 'costoBono' || eerrColumn === 'costoPrevired') {
            if (transaction.type === 'egreso') balanceImpact = -amount;
        } else if (eerrColumn === 'gastosGenerales') {
            if (transaction.type === 'egreso') {
                const catLower = transaction.category.toLowerCase();
                // Excluimos Remuneraciones, Salarios, Impuestos y HHEE antiguos de Gastos Generales
                const isRemunOrTax = ['remuneraciones', 'salario', 'impuestos directos'].includes(catLower) || catLower.includes('hhee');
                if (!isRemunOrTax) {
                    balanceImpact = -amount;
                }
            }
        } else if (eerrColumn === 'comisionesBancarias') {
            balanceImpact = -commission;
        } else if (eerrColumn === 'castigoIncobrables') {
             balanceImpact = -amount;
        }
    }

    return { ...transaction, balanceImpact };
  });

  if (filter.type === 'completado' && !searchParams.get('ids')) { 
    let runningBalance = 0;
    const calculated = [...transactionsWithBalanceImpact]
      .sort((a, b) => {
          const dateA = a.completion_date ? new Date(a.completion_date) : new Date(a.date);
          const dateB = b.completion_date ? new Date(b.completion_date) : new Date(b.date);
          if (dateA.getTime() === dateB.getTime()) {
              return a.id - b.id;
          }
          return dateA - dateB;
      })
      .map(transaction => {
        runningBalance += transaction.balanceImpact;
        return { ...transaction, runningBalance };
      });
    transactionsWithRunningBalance = calculated.reverse();
  } else {
    transactionsWithRunningBalance = transactionsWithBalanceImpact;
  }

  const selectedTotal = transactionsWithRunningBalance
    .filter(t => selectedIds.length === 0 || selectedIds.includes(t.id))
    .reduce((sum, t) => sum + t.balanceImpact, 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  };


  return (
    <Container style={{ maxWidth: '80%' }} className="py-4">
      <Stack gap={4}>
        <header className="p-3 bg-light text-dark rounded d-flex justify-content-between align-items-center shadow-sm border">
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <h1 className="fw-bold m-0">Gestor de Transacciones</h1>
          </Link>
          <div>
            {user && <span className="me-2 fw-bold text-muted">👤 {user.name || user.email}</span>}
            <ButtonGroup>
              {(SUPER_ADMIN_EMAILS.includes(user?.email?.trim()?.toLowerCase()) || 
                user?.email?.toLowerCase() === 'maria.soledad.fernandezi@gmail.com' || 
                user?.role === 'operaciones') && (
                <Link to="/admin/asistencia">
                  <Button variant="warning" className="fw-bold me-1">🕒 Reloj</Button>
                </Link>
              )}
              <Link to="/comisiones">
                <Button variant="outline-primary" className="me-1">Comisiones RD</Button>
              </Link>
              <Link to="/kpi-unpaid">
                <Button variant="outline-info" className="me-1">KPI Impago</Button>
              </Link>
              <Link to="/analytics">
                <Button variant="outline-success" className="me-1">Analítica</Button>
              </Link>
              {user?.email !== 'maria.soledad.fernandezi@gmail.com' && user?.role !== 'gestor' && (
                <Link to="/income-statement">
                  <Button variant="outline-warning" className="me-1">EERR</Button>
                </Link>
              )}
              <Link to="/global-wallet">
                <Button variant="outline-dark" className="me-1">Global 66</Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="primary" className="fw-bold me-1">📊 Cartera</Button>
              </Link>
              <Link to="/driver-payments">
                <Button variant="success" className="fw-bold me-1">💰 Pagos Conductores</Button>
              </Link>
              {canAccessPayroll && (
                <Button variant="warning" className="fw-bold me-1 text-dark shadow-sm" onClick={() => setShowPayrollModal(true)}>
                  🔐 Liquidaciones
                </Button>
              )}
              {(SUPER_ADMIN_EMAILS.includes(user?.email?.trim()?.toLowerCase()) || 
                user?.role === 'operaciones' || 
                user?.role === 'admin' || 
                user?.role === 'gestor') && (
                <Link to="/transport">
                  <Button variant="dark" className="me-1">🚖 Operaciones</Button>
                </Link>
              )}
              {isSuperAdminUser(user, originalAdminUser) && (
                <Button
                  variant="dark"
                  className="fw-bold me-1 shadow-sm"
                  style={{
                    background: 'linear-gradient(90deg, #0f172a 0%, #311042 100%)',
                    border: '1px solid #f59e0b',
                    color: '#f8fafc',
                  }}
                  onClick={openSuperAdminModal}
                  title="Entrar como cualquier proveedor, conductor o cliente sin clave"
                >
                  👁️ Ver como...
                </Button>
              )}
              <Button variant="outline-secondary" onClick={() => setShowLogModal(true)} className="me-1">Bitácora</Button>
              <Button variant="danger" onClick={logout}>Salir</Button>
            </ButtonGroup>
          </div>
        </header>

        <div>
          <h2>Resumen</h2>
          <Summary 
            transactions={transactions} 
            onCategoryFilterClick={handleSummaryCategoryFilterClick}
            onFilterClick={handleSummaryFilterClick}
          />
        </div>

        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={toggleTransactionForm}>
                    {showTransactionForm ? 'Ocultar Formulario' : 'Agregar Transacción'}
                </Button>
                <Button variant="info" onClick={() => setShowBulkUploadModal(true)} className="ms-2">
                    Carga Masiva de Transacciones
                </Button>
            </div>
            {showTransactionForm && (
                <>
                    <TransactionForm 
                        addTransaction={addSingleTransaction} 
                        editingTransaction={editingTransaction}
                        updateTransaction={updateTransaction}
                        cancelEdit={cancelEdit}
                        ref={formRef}
                    />
                    <hr />
                </>
            )}
            <FilterControls 
              currentFilter={filter.type} 
              onFilterChange={handleFilterChange} 
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              searchId={searchId}
              onSearchIdChange={setSearchId}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              sortByCompletionDate={sortByCompletionDate}
              onSortToggle={handleSortToggle}
              filteredData={transactionsWithRunningBalance}
              currentTypeFilter={filter.transactionType}
              onTypeFilterChange={handleTypeFilterChange}
              currentCategoryFilter={filter.category}
              onCategoryFilterChange={handleCategoryFilterClick}
              currentSubcategoryFilter={subCategoryFilter}
              onSubcategoryFilterChange={setSubCategoryFilter}
            />
        </div>

        {selectedIds.length > 0 && (
            <div className="bg-warning p-3 rounded mb-3 d-flex align-items-center gap-3 shadow-sm">
                <span className="fw-bold">{selectedIds.length} seleccionados</span>
                
                <select 
                    className="form-select w-auto"
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                >
                    <option value="">Cambiar Estado...</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="Por Procesar">Por Procesar</option>
                    <option value="Esperando OC/HES">Esperando OC/HES</option>
                    <option value="completado">Completado</option>
                    <option value="incobrable">Incobrable</option>
                </select>

                <select 
                    className="form-select w-auto"
                    value={bulkCategory}
                    onChange={(e) => {
                        setBulkCategory(e.target.value);
                        setBulkSubcategory('');
                    }}
                >
                    <option value="">Cambiar Categoría...</option>
                    <option>Remuneraciones</option>
                    <option>Gasto</option>
                    <option>Movimiento Financiero</option>
                    <option>Servicios</option>
                    <option>Transporte</option>
                    <option>Comida</option>
                    <option>Conductores</option>
                    <option>Particulares</option>
                    <option>Impuestos Directos</option>
                    <option>Otros</option>
                    <option>HHEE</option>
                    <option>HHEE Sábado</option>
                    <option>HHEE Domingo</option>
                </select>

                {['Gasto', 'Movimiento Financiero'].includes(bulkCategory) && (
                    <select
                        className="form-select w-auto"
                        value={bulkSubcategory}
                        onChange={(e) => setBulkSubcategory(e.target.value)}
                    >
                        <option value="">Sub-Categoría...</option>
                        {bulkCategory === 'Gasto' && SUB_CATEGORIES_EXPENSE.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                        {bulkCategory === 'Movimiento Financiero' && SUB_CATEGORIES_FINANCIAL.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                )}

                <Button variant="dark" onClick={handleBulkUpdate} disabled={!bulkStatus && !bulkCategory}>
                    Aplicar Cambios
                </Button>
            </div>
        )}

        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-3">
              <h2 className="m-0">Transacciones</h2>
              {!hasActiveFilters && (
                <span className="badge bg-secondary text-white fw-normal px-3 py-2 fs-6 shadow-sm">
                  ⏱️ Última semana (sin filtros)
                </span>
              )}
            </div>
            
            <div className="bg-light p-2 rounded border border-primary d-flex align-items-center shadow-sm">
              <span className="me-2 fw-bold text-primary">
                {selectedIds.length > 0 ? "Total Seleccionado:" : "Total Visible:"}
              </span>
              <span className="fs-5 fw-bold text-dark">
                {formatCurrency(selectedTotal)}
              </span>
              {selectedIds.length > 0 && (
                 <Button variant="link" size="sm" className="ms-2 text-decoration-none" onClick={() => setSelectedIds([])}>
                   (Limpiar Selección)
                 </Button>
              )}
            </div>

            <ButtonGroup aria-label="View mode">
              <Button variant={viewMode === 'list' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('list')}>Vista de Lista</Button>
              <Button variant={viewMode === 'table' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('table')}>Vista de Tabla</Button>
            </ButtonGroup>
          </div>
          {viewMode === 'list' ? (
            <TransactionList 
              transactions={transactionsWithRunningBalance} 
              deleteTransaction={deleteTransaction} 
              toggleComplete={toggleComplete} 
              handleSetEditing={handleSetEditing}
              cloneTransaction={cloneTransaction}
              handleShowLogs={handleShowTransactionLogs}
              currentFilter={filter.type}
              selectedIds={selectedIds}
              onToggleSelection={handleToggleSelection}
            />
          ) : (
            <TransactionTable 
              transactions={transactionsWithRunningBalance} 
              deleteTransaction={deleteTransaction} 
              toggleComplete={toggleComplete} 
              handleSetEditing={handleSetEditing}
              cloneTransaction={cloneTransaction}
              handleShowLogs={handleShowTransactionLogs}
            />
          )}
        </div>

      </Stack>

      <BulkUploadModal 
        show={showBulkUploadModal} 
        handleClose={() => setShowBulkUploadModal(false)} 
      />
      <LogModal 
        show={showLogModal} 
        handleClose={() => setShowLogModal(false)} 
        onGoToTransaction={handleGoToTransaction}
      />
      <LogModal 
        show={showTransactionLogsModal} 
        handleClose={() => setShowTransactionLogsModal(false)} 
        logs={transactionLogs} 
        title="Historial de la Transacción"
        onGoToTransaction={handleGoToTransaction}
      />
      <PayrollProcessorModal 
        show={showPayrollModal} 
        onHide={() => setShowPayrollModal(false)} 
        onTransactionsCreated={fetchTransactions} 
      />
      <InvoiceImpactModal
        show={showImpactModal}
        handleClose={() => {
          setShowImpactModal(false);
          setPendingConfirmAction(null);
        }}
        handleConfirm={() => {
          if (pendingConfirmAction) {
            pendingConfirmAction();
          }
          setShowImpactModal(false);
          setPendingConfirmAction(null);
        }}
        loading={impactModalLoading}
        targetStatus={impactTargetStatus}
        impacts={impactModalData}
      />
    </Container>
  );
};

function App() {
  const { initializeAuth, isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: '3.64'
  });

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Blindaje estricto de visibilidad para Emilio - Asistente EmiGo
  // Solo visible para personal operativo interno y NUNCA en portales externos ni de trabajadores
  const isOperationalStaff = ['admin', 'gestor', 'operaciones', 'locutor'].includes(user?.role);
  const isExcludedPortal = 
    location.pathname.startsWith('/driver') ||
    location.pathname.startsWith('/provider') ||
    location.pathname.startsWith('/portal') ||
    location.pathname.startsWith('/reloj') ||
    location.pathname.startsWith('/onboarding') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/reset-password') ||
    location.pathname.startsWith('/register-driver') ||
    location.pathname.startsWith('/pago-exitoso') ||
    location.pathname.startsWith('/comprobante') ||
    location.pathname.startsWith('/comprobante-pago');

  const showAssistant = isAuthenticated && isOperationalStaff && !isExcludedPortal;

  return (
    <>
      <SuperAdminImpersonator />
      <Routes>
        <Route path="/pago-exitoso" element={<PaymentSuccessPage />} />
        <Route path="/pago-exitoso/:trip_id" element={<PaymentSuccessPage />} />
        <Route path="/comprobante/:trip_id" element={<PaymentSuccessPage />} />
        <Route path="/comprobante" element={<PaymentSuccessPage />} />
        <Route path="/comprobante-pago" element={<PaymentSuccessPage />} />
        <Route path="/comprobante-pago/:trip_id" element={<PaymentSuccessPage />} />
        <Route path="/login" element={isAuthenticated ? <RoleBasedRedirect /> : <Login />} />
        <Route path="/register-driver" element={isAuthenticated ? <RoleBasedRedirect /> : <RegisterDriver />} />
        <Route path="/reset-password" element={isAuthenticated ? <RoleBasedRedirect /> : <ResetPassword />} />
        <Route path="/onboarding" element={<PrivateRoute><DriverOnboarding /></PrivateRoute>} />
        
        <Route path="/portal" element={<PrivateRoute><PortalLayout /></PrivateRoute>}>
            <Route index element={<PortalHome />} />
            <Route path="new-trip" element={<NewTripPage />} />
            <Route path="my-trips" element={<MyTripsPage />} />
        </Route>

        <Route path="/reloj" element={<PrivateRoute><MarkAttendance /></PrivateRoute>} />
        <Route path="/admin/asistencia" element={<PrivateRoute><AttendanceAdminWrapper /></PrivateRoute>} />

        <Route path="/driver" element={<PrivateRoute><DriverPortal isLoaded={isLoaded} /></PrivateRoute>} />
        <Route path="/provider" element={<PrivateRoute><ProviderPortal isLoaded={isLoaded} /></PrivateRoute>} />

        <Route path="/" element={<PrivateRoute requiredRole="admin"><MainApp /></PrivateRoute>} />
        <Route path="/global-wallet" element={<PrivateRoute requiredRole="admin"><GlobalWalletPage /></PrivateRoute>} />
        <Route path="/portfolio" element={<PrivateRoute requiredRole="admin"><PortfolioDashboard /></PrivateRoute>} />
        <Route path="/driver-payments" element={<PrivateRoute requiredRole="admin"><DriverPayments /></PrivateRoute>} />
        <Route path="/transport/*" element={<PrivateRoute requiredRole="admin"><TransportDashboard isLoaded={isLoaded} /></PrivateRoute>} />

        <Route path="/comisiones" element={<PrivateRoute requiredRole="admin"><CommissionsPage /></PrivateRoute>} />
        <Route path="/kpi-unpaid" element={<PrivateRoute requiredRole="admin"><KpiUnpaidPage /></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute requiredRole="admin"><AnalyticsPage /></PrivateRoute>} />
        <Route path="/income-statement" element={<PrivateRoute requiredRole="admin"><IncomeStatement /></PrivateRoute>} />
        
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      {showAssistant && <AssistantChatWidget />}
    </>
  );
}

export default App;
